import { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRecipes, useHouseholdSettings } from '../hooks/useSupabase';
import { useTonight } from '../hooks/useTonight';
import { getStartByTime, DEFAULT_MEAL_TIMES } from '../lib/mealTimeUtils';
import { trackEvent } from '../lib/analytics';

const SOURCE_LABEL = {
  catalog: 'Home cooked', saved: 'Home cooked', ai: 'Home cooked', user: 'Home cooked',
  leftover: 'Leftovers', frozen: 'Frozen', takeout: 'Takeout',
};

// Pretty-print an ingredient amount (whole → "2", common fractions → "½", else 1 decimal).
function formatAmount(amount) {
  if (amount === Math.floor(amount)) return String(amount);
  const fractions = { 0.25: '¼', 0.5: '½', 0.75: '¾', 0.33: '⅓', 0.67: '⅔' };
  const whole = Math.floor(amount);
  const frac = Math.round((amount - whole) * 100) / 100;
  if (fractions[frac]) return whole > 0 ? `${whole}${fractions[frac]}` : fractions[frac];
  return amount.toFixed(1).replace(/\.0$/, '');
}

// Mobile-first "what's for dinner tonight" — tonight's dinner_event from the
// board plan, with timing + steps (MEA-192). Calm and glanceable at 375px.
export default function TonightView() {
  const { householdId } = useAuth();
  const { recipes } = useRecipes(householdId);
  const { mealTimes } = useHouseholdSettings(householdId);
  const { event, loading, setTargetTime } = useTonight(householdId);
  const [pendingTime, setPendingTime] = useState(null);

  useEffect(() => { trackEvent('tonight_viewed'); }, []);

  const recipeMap = useMemo(() => Object.fromEntries((recipes || []).map((r) => [r.id, r])), [recipes]);
  const recipe = event?.recipe_id ? recipeMap[event.recipe_id] : null;
  const title = recipe?.name || event?.title || null;
  const hasDinner = event && event.status !== 'unplanned' && Boolean(title);

  const householdDinner = (mealTimes || DEFAULT_MEAL_TIMES).dinner || DEFAULT_MEAL_TIMES.dinner;
  const eventTime = event?.target_time ? event.target_time.slice(0, 5) : null; // "HH:MM:SS" → "HH:MM"
  // pendingTime = optimistic value shown right after an edit, before refetch lands.
  const dinnerTime = pendingTime ?? eventTime ?? householdDinner;
  const isOverride = dinnerTime !== householdDinner;
  const startBy = recipe ? getStartByTime(dinnerTime, recipe.prep_time_min, recipe.cook_time_min) : null;

  const changeTime = async (value) => {
    const next = value || householdDinner; // cleared input → back to the household default
    setPendingTime(next);
    trackEvent('tonight_time_adjusted');
    try {
      await setTargetTime(value || null);
    } catch {
      setPendingTime(null); // revert optimism on failure
    }
  };
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : [];
  const ingredients = Array.isArray(recipe?.ingredients) ? recipe.ingredients : [];
  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <div className="max-w-2xl mx-auto">
      <p className="font-body text-xs text-ink-muted mb-1">{dateLabel}</p>
      <h1 className="font-serif text-3xl lg:text-4xl text-ink mb-5">Tonight</h1>

      {loading ? (
        <p className="font-body text-sm text-ink-muted">Loading tonight…</p>
      ) : !hasDinner ? (
        <div className="bg-surface border border-hairline rounded-2xl p-6">
          <p className="font-body text-ink-secondary mb-4">
            {event?.is_busy ? 'A busy night — no dinner planned yet.' : 'No dinner planned for tonight.'}
          </p>
          <Link to="/planner" className="inline-flex items-center gap-1 font-body text-sm font-semibold text-primary-text">Open the plan →</Link>
        </div>
      ) : (
        <div className="bg-surface border border-hairline rounded-2xl p-5 lg:p-6">
          <h2 className="font-serif text-2xl lg:text-3xl text-ink leading-tight">{title}</h2>
          <p className="font-body text-sm text-ink-muted mt-1.5">
            {SOURCE_LABEL[event.source] || 'Home cooked'}
            {recipe?.prep_time_min ? ` · ${recipe.prep_time_min}m prep` : ''}
            {recipe?.cook_time_min ? ` · ${recipe.cook_time_min}m cook` : ''}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
            <label htmlFor="dinner-time" className="font-body text-sm text-ink-secondary">Dinner at</label>
            <input
              id="dinner-time"
              type="time"
              value={dinnerTime}
              onChange={(e) => changeTime(e.target.value)}
              className="font-body text-sm text-ink bg-surface border border-hairline rounded-lg px-2 py-1 focus:outline-none focus:border-primary"
              aria-label="Target dinner time tonight"
            />
            {isOverride && (
              <button
                type="button"
                onClick={() => changeTime('')}
                className="font-body text-xs text-ink-muted underline hover:text-ink-secondary"
              >
                reset to usual
              </button>
            )}
          </div>
          {startBy && <p className="font-body text-sm text-primary-text font-medium mt-1.5">Start by {startBy}</p>}
          {event.note && <p className="font-body text-sm text-ink-secondary mt-2">{event.note}</p>}

          {ingredients.length > 0 && (
            <div className="mt-5">
              <h3 className="font-serif text-lg text-ink mb-2">Ingredients</h3>
              <ul className="space-y-1">
                {ingredients.map((ing, i) => (
                  <li key={i} className="flex items-baseline gap-2 font-body text-sm text-ink">
                    <span className="text-ink-muted">•</span>
                    <span>
                      {ing.amount > 0 && <>{formatAmount(ing.amount)} </>}
                      {ing.unit && <>{ing.unit} </>}
                      {ing.item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {steps.length > 0 ? (
            <ol className="mt-5 space-y-3">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-primary-tint text-primary-text font-mono text-xs flex items-center justify-center">{i + 1}</span>
                  <span className="font-body text-sm text-ink leading-relaxed">
                    {s.text}{s.time ? <span className="text-ink-muted"> ({s.time})</span> : null}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="font-body text-sm text-ink-muted mt-4">
              {recipe ? 'No steps for this one.' : 'Nothing to cook — enjoy.'}
            </p>
          )}

          <Link to="/planner" className="inline-flex items-center gap-1 mt-6 font-body text-sm font-semibold text-primary-text">Change tonight on the plan →</Link>
        </div>
      )}
    </div>
  );
}
